import { useMemo } from "react";

import { colors } from "theme/palette";

import Box from "components/Box";
import Stack from "components/Stack";
import Tag from "components/Tag";
import Text from "components/Text";

import PrintableFieldTitle from "pages/PrintableChecklist/components/PrintableFieldTitle";
import PrintableFilesTable from "pages/PrintableChecklist/components/PrintableFilesTable";

import ExpandedButton from "entities/Field/components/Filefield/ExpandedButton";
import { IFilefieldCustomValue } from "entities/Field/components/Filefield/FilesTable";
import { EFieldStatus } from "entities/Field/constants";
import { IField } from "entities/Field/sdk";

export interface IPrintableFilefield {
  field: IField;
}

const PrintableFilefield = ({ field }: IPrintableFilefield) => {
  const customValues = useMemo(
    () =>
      field.custom_values?.map(
        (value) => value as unknown as IFilefieldCustomValue
      ) || [],
    [field]
  );

  const uploadedLabel = useMemo(() => {
    if (customValues.length === 1) {
      return `${customValues.length} file uploaded`;
    }
    return `${customValues.length} files uploaded`;
  }, [customValues]);

  return (
    <>
      <Box id={field.field_id.toString()}>
        <Stack
          direction="row"
          justifyContent="space-between"
          spacing={2}
          sx={{
            padding: 0.5,
            borderBottom: `1px solid ${colors.blue20}`,
            backgroundColor: "initial",
          }}
        >
          <PrintableFieldTitle field={field} />
          <Stack direction="row" spacing={1} paddingTop={0.5}>
            {field.status !== EFieldStatus.NA && (
              <Stack flexDirection="row" alignItems="center" gap={1}>
                {customValues.length > 0 && (
                  <ExpandedButton
                    label={uploadedLabel}
                    isOpen
                    onClick={() => {}}
                    displayExpandIcon={false}
                  />
                )}
                {customValues.length === 0 && (
                  <Tag
                    label="missing"
                    variant="red"
                    sx={{ alignSelf: "center" }}
                  />
                )}
              </Stack>
            )}
            {field.status === EFieldStatus.NA && (
              <Text sx={{ color: colors.gray60 }}>N/A</Text>
            )}
          </Stack>
        </Stack>
        {customValues.length > 0 && (
          <Box
            sx={{
              marginTop: 1,
              paddingLeft: 6,
            }}
          >
            <Tag
              label="file uploads"
              variant="gray20"
              sx={{ display: "inline-block", marginBottom: 1 }}
              dataTestid="uploaded-fields-tag-label"
            />
            <PrintableFilesTable customFieldValues={customValues} />
          </Box>
        )}
      </Box>
    </>
  );
};

export default PrintableFilefield;
